import React, { useState } from 'react';
import { Banknote, X } from 'lucide-react';
import { apiCall, generateId, formatPrice, printRawHtml } from '../utils/utils.js';

// PKR denomination structure
const NOTES = [5000, 1000, 500, 100, 50, 20, 10];
const COINS = [5, 2, 1];

function emptyDenom() {
  const d = {};
  [...NOTES, ...COINS].forEach(n => { d[n] = ''; });
  return d;
}

function calcTotal(denom) {
  return [...NOTES, ...COINS].reduce((sum, n) => sum + (parseInt(denom[n] || 0) * n), 0);
}

const DenomGrid = ({ denom, onChange, label }) => (
  <div>
    <p className="text-[11px] font-bold text-[#5c5f62] uppercase tracking-wider mb-2">{label}</p>
    <div className="space-y-1">
      {NOTES.map(n => (
        <div key={n} className="flex items-center gap-2">
          <span className="w-16 text-right text-xs font-semibold text-[#1a1a1a]">Rs {n.toLocaleString()}</span>
          <span className="text-[10px] text-[#5c5f62]">×</span>
          <input
            type="number" min="0"
            value={denom[n]}
            onChange={e => onChange(n, e.target.value)}
            className="w-16 text-center p-1 border border-[#c9cccf] rounded text-xs font-bold outline-none focus:border-[#008060]"
            placeholder="0"
          />
          <span className="text-[10px] text-[#5c5f62] w-20 text-right">
            {denom[n] ? `= Rs ${(parseInt(denom[n]||0)*n).toLocaleString()}` : ''}
          </span>
        </div>
      ))}
      <div className="border-t border-dashed border-[#e1e3e5] my-1"/>
      {COINS.map(n => (
        <div key={n} className="flex items-center gap-2">
          <span className="w-16 text-right text-xs font-semibold text-[#1a1a1a]">Rs {n}</span>
          <span className="text-[10px] text-[#5c5f62]">×</span>
          <input
            type="number" min="0"
            value={denom[n]}
            onChange={e => onChange(n, e.target.value)}
            className="w-16 text-center p-1 border border-[#c9cccf] rounded text-xs font-bold outline-none focus:border-[#008060]"
            placeholder="0"
          />
          <span className="text-[10px] text-[#5c5f62] w-20 text-right">
            {denom[n] ? `= Rs ${(parseInt(denom[n]||0)*n).toLocaleString()}` : ''}
          </span>
        </div>
      ))}
    </div>
    <div className="mt-2 pt-2 border-t border-[#e1e3e5] flex justify-between items-center">
      <span className="text-xs font-bold text-[#1a1a1a]">Total</span>
      <span className="text-sm font-black text-[#008060]">{formatPrice(calcTotal(denom))}</span>
    </div>
  </div>
);

// ── Open shift modal ──────────────────────────────────────────────────────────
const ShiftModal = ({ currentProfile, openShifts, setOpenShifts }) => {
  const [denom, setDenom]   = useState(emptyDenom);
  const [loading, setLoading] = useState(false);
  const [mode, setMode]     = useState('denom'); // 'denom' | 'manual'
  const [manualCash, setManualCash] = useState('');

  const openingCash = mode === 'denom' ? calcTotal(denom) : parseFloat(manualCash || 0);

  const handleDenomChange = (note, val) => setDenom(prev => ({ ...prev, [note]: val }));

  const handleOpen = async () => {
    setLoading(true);
    const shiftData = {
      id: generateId(),
      profile_id: currentProfile,
      opening_cash: openingCash,
      opening_denom: mode === 'denom' ? JSON.stringify(denom) : null,
    };
    await apiCall('openShift', shiftData);
    setOpenShifts(prev => [...prev, { ...shiftData, status: 'Open', opened_at: new Date().toISOString() }]);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-[#1a1a1a]/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-xl shadow-xl border border-[#e1e3e5] flex flex-col max-h-[95vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#e1e3e5] bg-[#f6f7f8]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#e4fcf4] text-[#008060] rounded-full flex items-center justify-center"><Banknote size={16}/></div>
            <span className="font-bold text-[#1a1a1a]">Open Register</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMode('denom')} className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${mode==='denom' ? 'bg-[#008060] text-white' : 'bg-[#e1e3e5] text-[#5c5f62]'}`}>Count Cash</button>
            <button onClick={() => setMode('manual')} className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${mode==='manual' ? 'bg-[#008060] text-white' : 'bg-[#e1e3e5] text-[#5c5f62]'}`}>Enter Total</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {mode === 'denom' ? (
            <DenomGrid denom={denom} onChange={handleDenomChange} label="Count your opening cash"/>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <Banknote size={40} className="text-[#008060]"/>
              <p className="text-sm text-[#5c5f62]">Enter the total physical cash in the drawer.</p>
              <input
                type="number" placeholder="Opening Cash (₨)"
                value={manualCash}
                onChange={e => setManualCash(e.target.value)}
                className="w-full text-center text-2xl font-bold p-3 border border-[#c9cccf] rounded-lg outline-none focus:border-[#008060]"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#e1e3e5] bg-[#f6f7f8]">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-bold text-[#1a1a1a]">Opening Cash</span>
            <span className="text-xl font-black text-[#008060]">{formatPrice(openingCash)}</span>
          </div>
          <button
            onClick={handleOpen}
            disabled={loading || openingCash < 0}
            className="w-full bg-[#008060] text-white py-3 rounded-lg font-semibold text-base disabled:opacity-50 hover:bg-[#006e52] transition-colors"
          >
            {loading ? 'Opening…' : 'Start Shift'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftModal;

// ── Close shift modal (exported for use in App.jsx) ───────────────────────────
export const CloseShiftModal = ({ shift, onClose, storeSettings }) => {
  const [denom, setDenom]   = useState(emptyDenom);
  const [mode, setMode]     = useState('denom');
  const [manualCash, setManualCash] = useState('');
  const [loading, setLoading] = useState(false);

  const closingCash   = mode === 'denom' ? calcTotal(denom) : parseFloat(manualCash || 0);
  const expectedCash  = parseFloat(shift?.expected_cash || shift?.opening_cash || 0);
  const variance      = closingCash - expectedCash;

  const handleDenomChange = (note, val) => setDenom(prev => ({ ...prev, [note]: val }));

  const printZReport = (closingC) => {
    const storeName   = storeSettings?.storeName || storeSettings?.store_name || 'OPS POS';
    const openedAt    = shift?.opened_at ? new Date(shift.opened_at).toLocaleString('en-PK') : '—';
    const closedAt    = new Date().toLocaleString('en-PK');
    const openingC    = parseFloat(shift?.opening_cash || 0);
    const varAmt      = closingC - expectedCash;
    const denomRows   = mode === 'denom'
      ? [...NOTES, ...COINS].filter(n => parseInt(denom[n]||0) > 0).map(n =>
          `<tr><td>Rs ${n.toLocaleString()} × ${parseInt(denom[n])}</td><td style="text-align:right">Rs ${(n*parseInt(denom[n])).toLocaleString()}</td></tr>`
        ).join('')
      : '';

    printRawHtml(`<!DOCTYPE html><html><head><meta charset="utf-8">
<style>
@page{size:80mm auto;margin:4mm}
body{font-family:'Courier New',monospace;font-size:11px;color:#000;width:72mm}
.c{text-align:center}.b{font-weight:bold}.hr{border-top:1px dashed #000;margin:5px 0}
table{width:100%;border-collapse:collapse}td{vertical-align:top;padding:1px 0}
.big{font-size:14px}.ok{color:#008060}.bad{color:#cc0000}
</style></head><body>
<div class="c b big">${storeName}</div>
<div class="c" style="font-size:10px">Z-Report / Shift Close</div>
<div class="hr"></div>
<table>
  <tr><td>Opened</td><td style="text-align:right">${openedAt}</td></tr>
  <tr><td>Closed</td><td style="text-align:right">${closedAt}</td></tr>
  ${shift?.opened_by ? `<tr><td>Staff</td><td style="text-align:right">${shift.opened_by}</td></tr>` : ''}
</table>
<div class="hr"></div>
<table>
  <tr><td>Opening Cash</td><td style="text-align:right">Rs ${openingC.toLocaleString()}</td></tr>
  <tr><td>Expected Cash</td><td style="text-align:right">Rs ${expectedCash.toLocaleString()}</td></tr>
  <tr class="b"><td>Closing Cash</td><td style="text-align:right">Rs ${closingC.toLocaleString()}</td></tr>
  <tr><td>Variance</td><td style="text-align:right" class="${varAmt>=0?'ok':'bad'}">${varAmt>=0?'+':''}Rs ${varAmt.toLocaleString()}</td></tr>
</table>
${denomRows ? `<div class="hr"></div><div class="b" style="font-size:10px">Denomination Breakdown</div><table>${denomRows}</table>` : ''}
<div class="hr"></div>
<div class="c" style="font-size:10px">Shift closed successfully</div>
</body></html>`);
  };

  const handleClose = async () => {
    setLoading(true);
    await apiCall('closeShift', {
      id: shift.id,
      expected_cash: expectedCash,
      closing_cash: closingCash,
      closing_denom: mode === 'denom' ? JSON.stringify(denom) : null,
    });
    setLoading(false);
    printZReport(closingCash);
    onClose(closingCash);
  };

  return (
    <div className="fixed inset-0 bg-[#1a1a1a]/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-xl shadow-xl border border-[#e1e3e5] flex flex-col max-h-[95vh] overflow-hidden">

        <div className="flex items-center justify-between p-4 border-b border-[#e1e3e5] bg-[#f6f7f8]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#fbeae5] text-[#d82c0d] rounded-full flex items-center justify-center"><Banknote size={16}/></div>
            <span className="font-bold text-[#1a1a1a]">Close Register</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMode('denom')} className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${mode==='denom' ? 'bg-[#008060] text-white' : 'bg-[#e1e3e5] text-[#5c5f62]'}`}>Count Cash</button>
            <button onClick={() => setMode('manual')} className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${mode==='manual' ? 'bg-[#008060] text-white' : 'bg-[#e1e3e5] text-[#5c5f62]'}`}>Enter Total</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-[#f6f7f8] rounded-lg p-3 mb-4 flex justify-between text-sm">
            <span className="text-[#5c5f62] font-semibold">Expected Cash</span>
            <span className="font-black text-[#1a1a1a]">{formatPrice(expectedCash)}</span>
          </div>

          {mode === 'denom' ? (
            <DenomGrid denom={denom} onChange={handleDenomChange} label="Count your closing cash"/>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <Banknote size={40} className="text-[#d82c0d]"/>
              <input
                type="number" placeholder="Closing Cash (₨)"
                value={manualCash}
                onChange={e => setManualCash(e.target.value)}
                className="w-full text-center text-2xl font-bold p-3 border border-[#c9cccf] rounded-lg outline-none focus:border-[#008060]"
              />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#e1e3e5] bg-[#f6f7f8]">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-bold text-[#1a1a1a]">Closing Cash</span>
            <span className="text-xl font-black text-[#1a1a1a]">{formatPrice(closingCash)}</span>
          </div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs text-[#5c5f62]">Variance</span>
            <span className={`text-sm font-black ${variance === 0 ? 'text-[#008060]' : variance > 0 ? 'text-[#008060]' : 'text-[#d82c0d]'}`}>
              {variance >= 0 ? '+' : ''}{formatPrice(variance)}
            </span>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="w-full bg-[#d82c0d] text-white py-3 rounded-lg font-semibold text-base disabled:opacity-50 hover:bg-[#b0200c] transition-colors"
          >
            {loading ? 'Closing…' : 'Close Shift & Print Z-Report'}
          </button>
        </div>
      </div>
    </div>
  );
};
